import { Box, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import WidgetCard from '../WidgetCard/WidgetCard'
import { HeroColorKey } from '../WidgetCard/HeroContext'
import KPIProgress from './KPIProgress'
import { colors } from 'constants/theme'

export interface KPICardProps {
  title: string
  value: string
  subtitle?: string
  progressPct?: number | null
  color?: 'success' | 'warning' | 'error' | 'default'
  caption?: ReactNode
  headerAction?: ReactNode
  valueAdornment?: ReactNode
  // Async-state slots — forwarded to WidgetCard so loading/error/empty keep
  // the card chrome (including hero gradient) visible. Closes Z.1.
  loading?: boolean
  error?: Error | null
  empty?: boolean
  onRetry?: () => void
  skeletonHeight?: number
  // When set, the card renders with the hero gradient and value text is
  // forced to white (achievementColor signal is suppressed).
  heroColor?: HeroColorKey
}

const VALUE_COLOR: Record<NonNullable<KPICardProps['color']>, string> = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  default: 'text.primary',
}

export default function KPICard({
  title,
  value,
  subtitle,
  progressPct,
  color = 'default',
  caption,
  headerAction,
  valueAdornment,
  loading,
  error,
  empty,
  onRetry,
  skeletonHeight = 140,
  heroColor,
}: KPICardProps) {
  const onHero = !!heroColor
  const valueColor = onHero ? colors.white : VALUE_COLOR[color]

  return (
    <WidgetCard
      title={title}
      subtitle={subtitle}
      headerAction={headerAction}
      loading={loading}
      error={error}
      empty={empty}
      onRetry={onRetry}
      skeletonHeight={skeletonHeight}
      variant={onHero ? 'hero' : 'plain'}
      heroColor={heroColor}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: valueColor, lineHeight: 1.1 }}
        >
          {value}
        </Typography>
        {valueAdornment}
      </Box>
      {progressPct != null && (
        <Box sx={{ mt: 1 }}>
          <KPIProgress achievedPercent={progressPct} />
        </Box>
      )}
      {caption && <Box sx={{ mt: 1 }}>{caption}</Box>}
    </WidgetCard>
  )
}
